import { Button } from '@/components/ui/button';
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from '@/components/ui/separator';
import { cn } from "@/lib/utils";
import { generateCSAWCertificatePDF } from '@/utils/pdfGenerator';
import { gql, useMutation, useQuery } from '@apollo/client';
import { format } from "date-fns";
import { Calendar as CalendarIcon, Eye, FileText, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import COVCertificateTemplate from '../../CertificateComponents/certificateTemplates/COVCertificateTemplate';
import CSAWCertificateTemplate from '../../CertificateComponents/certificateTemplates/CSAWCertificateTemplate';
import PLTCPCertificateTemplate from '../../CertificateComponents/certificateTemplates/PLTCPCertificateTemplate';
import PLTPCertificateTemplate from '../../CertificateComponents/certificateTemplates/PLTPCertificateTemplate';
import PTPRCertificateTemplate from '../../CertificateComponents/certificateTemplates/PTPRCertificateTemplate';
import TCEBPCertificateTemplate from '../../CertificateComponents/certificateTemplates/TCEBPCertificateTemplate';

const GENERATE_CERTIFICATE = gql`
   mutation GenerateCertificate($input: GenerateCertificateInput!) {
      generateCertificate(input: $input) {
         id
         certificateNumber
         applicationId
         applicationType
         certificateStatus
         dateCreated
         csawCertificateData {
            registrationType
            ownerName
            address
            purpose
            chainsawDetails {
               brand
               model
               serialNumber
               dateOfAcquisition
               powerOutput
               maxLengthGuidebar
               countryOfOrigin
               purchasePrice
            }
         }
         covCertificateData {
            applicationNumber
            name
            driverName
            driverAddress
            originAddress
            destinationAddress
            forestProducts {
               kind
               species
               scientificName
               numberOfTrees
               quantity {
                  value
                  unit
               }
               volume {
                  cubicMeters
                  boardFeet
               }
            }
            conveyance
            vehiclePlateNumber
            consignee
            permitNumber # this is applicationNumber
         }
         ptprCertificateData {
            ownerName
            address
            contactNumber
            email
            octTctNumber
            lotNumber
            barangay
            municipality
            province
            totalLotArea
            areaDevotedToPlantation
            spacing
            treeSpeciesPlanted
            datePlanted
         }
         pltpCertificateData {
            name
            address
            lotNumber
            octNumber
            treeInventory {
               species
               scientificName
               numberOfTreesInventoried
               grossVolume
               netVolume
               treeStatus
            }
            totalTrees
            totalGrossVolume
            totalNetVolume
         }
         pltcpCertificateData {
            name
            occupation
            projectLocation
            projectAddress
            purpose
            treeInventory {
               species
               scientificName
               numberOfTreesInventoried
               grossVolume
               netVolume
               treeCategory
            }
            totalTrees
            totalGrossVolume
            totalNetVolume
         }
         tcebpCertificateData {
            name
            officeName
            officeAddress
            projectLocation
            contactNumber
            purpose
            requestType
            treeInventory {
               species
               numberOfTreesInventoried
               status
               treeCategory
            }
            totalTrees
         }
         applicantSignature {
            data
            contentType
         }
         orderOfPayment {
            officialReceipt {
               orNumber
               amount
               dateIssued
            }
         }
      }
   }
`;

const GET_CSAW_PERMIT = gql`
   query GetCSAWPermit($id: ID!) {
      getCSAWPermitById(id: $id) {
         id
         applicationNumber
         applicationType
         registrationType
         ownerName
         address
         purpose
         phone
         brand
         model
         serialNumber
         dateOfAcquisition
         powerOutput
         maxLengthGuidebar
         countryOfOrigin
         purchasePrice
         hasCertificate
         certificateId
      }
   }
`;

const GET_COV_PERMIT = gql`
   query GetCOVPermit($id: ID!) {
      getCOVPermitById(id: $id) {
         id
         applicationNumber
         applicationType
         name
         address
         cellphone
         purpose
         driverAddress
         driverName
         driverLicenseNumber
         vehiclePlateNumber
         originAddress
         destinationAddress
         hasCertificate
         certificateId
      }
   }
`;

const GET_PTPR_PERMIT = gql`
   query GetPTPRPermit($id: ID!) {
      getPTPRPermitById(id: $id) {
         id
         applicationNumber
         applicationType
         ownerName
         address
         contactNumber
         lotArea
         treeSpecies
         totalTrees
         treeSpacing
         yearPlanted
         hasCertificate
         certificateId
      }
   }
`;

const GET_PLTCP_PERMIT = gql`
   query GetPLTCPPermit($id: ID!) {
      getPLTCPPermitById(id: $id) {
         id
         applicationNumber
         applicationType
         name
         address
         contactNumber
         occupation
         projectLocation
         projectAddress
         purpose
         treeType
         treeStatus
         landType
         posingDanger
         forPersonalUse
         isWithinSubdivision
         isSchoolOrOrganization
         hasCertificate
         certificateId
      }
   }
`;

const GET_PLTP_PERMIT = gql`
   query GetPLTPPermit($id: ID!) {
      getPLTPPermitById(id: $id) {
         id
         applicationNumber
         applicationType
         requestType
         name
         address
         contactNumber
         treeType
         treeStatus
         landType
         posingDanger
         forPersonalUse
         purpose
         hasCertificate
         certificateId
      }
   }
`;

const GET_TCEBP_PERMIT = gql`
   query GetTCEBPPermit($id: ID!) {
      getTCEBPPermitById(id: $id) {
         id
         applicationNumber
         applicationType
         requestType
         name
         projectLocation
         officeName
         officeAddress
         contactNumber
         purpose
         hasCertificate
         certificateId
      }
   }
`;

const UPDATE_PERMIT_STAGE = gql`
   mutation UpdatePermitStage(
      $id: ID!,
      $currentStage: String!,
      $status: String!,
      $awaitingPermitCreation: Boolean,
      $PermitCreated: Boolean,
      $hasCertificate: Boolean,
      $certificateId: ID
   ) {
      updatePermitStage(
         id: $id,
         currentStage: $currentStage,
         status: $status,
         awaitingPermitCreation: $awaitingPermitCreation,
         PermitCreated: $PermitCreated,
         hasCertificate: $hasCertificate,
         certificateId: $certificateId
      ) {
         id
         currentStage
         status
         awaitingPermitCreation
         PermitCreated
         hasCertificate
         certificateId
      }
   }
`;

const GET_CERTIFICATE = gql`
   query GetCertificate($applicationId: ID!) {
      getCertificatesByApplicationId(applicationId: $applicationId) {
         id
         certificateNumber
         certificateStatus
         dateCreated
         dateIssued
         expiryDate
         dateApproved
         csawCertificateData {
            registrationType
            ownerName
            address
            purpose
            chainsawDetails {
               brand
               model
               serialNumber
               dateOfAcquisition
               powerOutput
               maxLengthGuidebar
               countryOfOrigin
               purchasePrice
            }
         }
         covCertificateData {
            applicationNumber
            name
            driverName
            driverAddress
            originAddress
            destinationAddress
            forestProducts {
               kind
               species
               scientificName
               numberOfTrees
               quantity {
                  value
                  unit
               }
               volume {
                  cubicMeters
                  boardFeet
               }
            }
            conveyance
            vehiclePlateNumber
            consignee
            permitNumber
         }
         ptprCertificateData {
            ownerName
            address
            contactNumber
            email
            octTctNumber
            lotNumber
            barangay
            municipality
            province
            totalLotArea
            areaDevotedToPlantation
            spacing
            treeSpeciesPlanted
            datePlanted
         }
         pltpCertificateData {
            name
            address
            lotNumber
            octNumber
            treeInventory {
               species
               scientificName
               numberOfTreesInventoried
               treeStatus
               grossVolume
               netVolume
            }
            totalTrees
            totalGrossVolume
            totalNetVolume
         }
         pltcpCertificateData {
            name
            occupation
            projectLocation
            projectAddress
            purpose
            treeInventory {
               species
               scientificName
               numberOfTreesInventoried
               grossVolume
               netVolume
               treeCategory
            }
            totalTrees
            totalGrossVolume
            totalNetVolume
         }
         tcebpCertificateData {
            name
            officeName
            officeAddress
            projectLocation
            contactNumber
            purpose
            requestType
            treeInventory {
               species
               numberOfTreesInventoried
               status
               treeCategory
            }
            totalTrees
         }
         applicantSignature {
            data
            contentType
         }
         orderOfPayment {
            officialReceipt {
               orNumber
               amount
               dateIssued
            }
         }
      }
   }
`;

const FORWARD_CERTIFICATE = gql`
   mutation ForwardCertificate($id: ID!, $applicationType: String!) {
      forwardCertificate(id: $id, applicationType: $applicationType) {
         id
         certificateNumber
         certificateStatus
         dateIssued
         expiryDate
         dateApproved
         applicationType
      }
   }
`;

const CHECK_OOP_STATUS = gql`
   query CheckOOPStatus($applicationId: ID!) {
      getOOPsByApplicationId(applicationId: $applicationId) {
         OOPstatus
      }
   }
`;

const GET_INSPECTION_REPORT = gql`
   query GetInspectionReport($permitId: ID!) {
      getInspectionsByPermit(permitId: $permitId) {
         id
         createdAt
         findings {
            result
            ptprFindings {
               octTctNumber
               lotNumber
               barangay
               municipality
               province
               totalLotArea
               areaDevotedToPlantation
               spacing
               treeSpeciesPlanted
               datePlanted
            }
            covFindings {
               kind
               species
               scientificName
               numberOfTrees
               quantity {
                  value
                  unit
               }
               volume {
                  cubicMeters
                  boardFeet
               }
               conveyance
               consignee
            }
            pltpFindings {
               treeInventory {
                  species
                  scientificName
                  numberOfTreesInventoried
                  grossVolume
                  netVolume
                  treeStatus
               }
               totalTrees
               totalGrossVolume
               totalNetVolume
               lotNumber
               octNumber
            }
            pltcpFindings {
               treeInventory {
                  species
                  scientificName
                  numberOfTreesInventoried
                  grossVolume
                  netVolume
                  treeCategory
               }
               totalTrees
               totalGrossVolume
               totalNetVolume
            }
            tcebpFindings {
               treeInventory {
                  species
                  numberOfTreesInventoried
                  status
                  treeCategory
               }
               totalTrees
            }
         }
      }
   }
`;

const TS_GenerateECertificateModal = ({ isOpen, onClose, application, onComplete }) => {
   const [metadata, setMetadata] = useState({
      issueDate: '',
      remarks: ''
   });
   const [generatedCertificate, setGeneratedCertificate] = useState(null);
   const certificateRef = useRef();
   const [showECertificate, setShowECertificate] = useState(false);
   const [isGenerating, setIsGenerating] = useState(false);

   const [generateCertificateMutation] = useMutation(GENERATE_CERTIFICATE);
   const [updatePermitStage] = useMutation(UPDATE_PERMIT_STAGE);
   const [forwardCertificate] = useMutation(FORWARD_CERTIFICATE);

   const { data: csawData, loading: csawLoading } = useQuery(GET_CSAW_PERMIT, {
      variables: { id: application.id },
      skip: !application.id || application.applicationType !== 'Chainsaw Registration',
   });

   const { data: covData, loading: covLoading } = useQuery(GET_COV_PERMIT, {
      variables: { id: application.id },
      skip: !application.id || application.applicationType !== 'Certificate of Verification',
   });

   const { data: ptprData, loading: ptprLoading } = useQuery(GET_PTPR_PERMIT, {
      variables: { id: application.id },
      skip: !application.id || application.applicationType !== 'Private Tree Plantation Registration',
   });

   const { data: pltcpData, loading: pltcpLoading } = useQuery(GET_PLTCP_PERMIT, {
      variables: { id: application.id },
      skip: !application.id || application.applicationType !== 'Public Land Tree Cutting Permit'
   });

   const { data: pltpData, loading: pltpLoading } = useQuery(GET_PLTP_PERMIT, {
      variables: { id: application.id },
      skip: !application.id || application.applicationType !== 'Private Land Timber Permit',
   });

   const { data: tcebpData, loading: tcebpLoading } = useQuery(GET_TCEBP_PERMIT, {
      variables: { id: application.id },
      skip: !application.id || application.applicationType !== 'Tree Cutting and/or Earth Balling Permit',
   });

   const { data: oopData } = useQuery(CHECK_OOP_STATUS, {
      variables: { applicationId: application?.id },
      skip: !application?.id
   });

   const { data: inspectionData } = useQuery(GET_INSPECTION_REPORT, {
      variables: { permitId: application.id },
      skip: !application.id || !['Certificate of Verification', 'Private Tree Plantation Registration', 'Private Land Timber Permit', 'Public Land Tree Cutting Permit', 'Tree Cutting and/or Earth Balling Permit'].includes(application.applicationType),
   });

   const applicationData = useMemo(() => {
      switch (application.applicationType) {
         case 'Chainsaw Registration':
            return csawData?.getCSAWPermitById;
         case 'Certificate of Verification':
            return covData?.getCOVPermitById;
         case 'Private Tree Plantation Registration':
            return ptprData?.getPTPRPermitById;
         case 'Public Land Tree Cutting Permit':
            return pltcpData?.getPLTCPPermitById;
         case 'Private Land Timber Permit':
            return pltpData?.getPLTPPermitById;
         case 'Tree Cutting and/or Earth Balling Permit':
            return tcebpData?.getTCEBPPermitById;
         default:
            return null;
      }
   }, [csawData, covData, ptprData, pltpData, pltcpData, tcebpData, application.applicationType]);

   const isLoading = (() => {
      switch (application.applicationType) {
         case 'Chainsaw Registration':
            return csawLoading;
         case 'Certificate of Verification':
            return covLoading;
         case 'Private Tree Plantation Registration':
            return ptprLoading;
         case 'Public Land Tree Cutting Permit':
            return pltcpLoading;
         case 'Private Land Timber Permit':
            return pltpLoading;
         case 'Tree Cutting and/or Earth Balling Permit':
            return tcebpLoading;
         default:
            return false;
      }
   })();

   const handleMetadataChange = (e) => {
      const { name, value } = e.target;
      setMetadata(prev => ({
         ...prev,
         [name]: value
      }));
   };

   const navigate = useNavigate();

   // Temporary handler for e-certificate generation
   const handleTemporaryECertGeneration = async () => {
      if (application.applicationType === 'Chainsaw Registration') {
         // For CSAW, proceed with normal generation
         return false; // Return false to indicate we should use normal flow
      }

      // For all other types, show WIP message
      toast.info('E-Certificate generation for ' + application.applicationType + ' is currently under development');
      return true; // Return true to indicate we handled it
   };

   const handlePrint = () => {
      // Determine the correct print page based on application type
      let printPath;
      let printData;

      switch (application.applicationType) {
         case 'Chainsaw Registration':
            printPath = '/personnel/csaw-certificate-print';
            printData = {
               certificate: generatedCertificate,
               orderOfPayment: generatedCertificate.orderOfPayment
            };
            break;
         case 'Certificate of Verification':
            printPath = '/personnel/cov-certificate-print';
            printData = {
               certificate: generatedCertificate,
               certificateData: generatedCertificate.covCertificateData,
               orderOfPayment: generatedCertificate.orderOfPayment
            };
            break;
         case 'Private Tree Plantation Registration':
            printPath = '/personnel/ptpr-certificate-print';
            printData = {
               certificate: generatedCertificate,
               certificateData: generatedCertificate.ptprCertificateData,
               orderOfPayment: generatedCertificate.orderOfPayment
            };
            break;
         case 'Public Land Tree Cutting Permit':
            printPath = '/personnel/pltcp-certificate-print';
            printData = {
               certificate: generatedCertificate,
               certificateData: generatedCertificate.pltcpCertificateData,
               orderOfPayment: generatedCertificate.orderOfPayment,
               pageNumber: 1 // Default to page 1
            };
            break;
         case 'Private Land Timber Permit':
            printPath = '/personnel/pltp-certificate-print';
            printData = {
               certificate: generatedCertificate,
               certificateData: generatedCertificate.pltpCertificateData,
               orderOfPayment: generatedCertificate.orderOfPayment
            };
            break;
         case 'Tree Cutting and/or Earth Balling Permit':
            printPath = '/personnel/tcebp-certificate-print';
            printData = {
               certificate: generatedCertificate,
               certificateData: generatedCertificate.tcebpCertificateData,
               orderOfPayment: generatedCertificate.orderOfPayment
            };
            break;
         default:
            toast.error(`Print not supported for ${application.applicationType}`);
            return;
      }

      navigate(printPath, { state: printData });
   };

   const handlePrintPage2 = () => {
      // Only for PLTCP certificates
      if (application.applicationType !== 'Public Land Tree Cutting Permit') {
         return;
      }

      navigate('/personnel/pltcp-certificate-print', {
         state: {
            certificate: generatedCertificate,
            certificateData: generatedCertificate.pltcpCertificateData,
            orderOfPayment: generatedCertificate.orderOfPayment,
            pageNumber: 2
         }
      });
   };

   const handlePrintTerms = () => {
      navigate('/personnel/csaw-terms-print');
   };

   const generateECertificate = async () => {
      if (isLoading) {
         toast.error('Loading application details...');
         return;
      }

      // Debug log application data
      console.log('Generate E-Certificate Debug:', {
         applicationTypeRequested: application.applicationType,
         applicationId: application.id,
         applicationData,
         tcebpData,
         inspectionData
      });

      if (!applicationData) {
         toast.error('Application details not found');
         return;
      }

      setIsGenerating(true);
      try {
         // Check if this is handled by temporary patch
         // const isHandledByTemp = await handleTemporaryECertGeneration();
         // if (isHandledByTemp) {
         //    return;
         // }

         if (applicationData.hasCertificate) {
            toast.error('Certificate already exists for this application');
            return;
         }

         // check OOP status
         const oopStatus = oopData?.getOOPsByApplicationId?.[0]?.OOPstatus;
         const isOOPComplete = ['Completed OOP', 'Issued OR'].includes(oopStatus);

         if (!isOOPComplete && application.applicationType !== 'Private Tree Plantation Registration') { // PTPR does not require OOP
            toast.error("Cannot generate certificate without completed Order of Payment");
            return;
         }

         // check inspection findings
         if (application.applicationType === 'Certificate of Verification') {
            const inspections = inspectionData?.getInspectionsByPermit || [];
            const latestInspection = inspections.length > 0
               ? inspections.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]
               : null;
            const covFindings = latestInspection?.findings?.covFindings;

            if (!covFindings) {
               toast.error('Inspection findings not found');
               return;
            }

            console.log('Inspection Findings:', covFindings);
            console.log('Volume Data:', covFindings.volume);

         } else if (application.applicationType === 'Private Tree Plantation Registration') {
            const ptprInspections = inspectionData?.getInspectionsByPermit || [];
            const latestPtprInspection = ptprInspections.length > 0
               ? ptprInspections.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]
               : null;
            const ptprFindings = latestPtprInspection?.findings?.ptprFindings;

            if (!ptprFindings) {
               toast.error('Inspection findings not found');
               return;
            }

            console.log('Inspection Findings:', ptprFindings);
         } else if (application.applicationType === 'Private Land Timber Permit') {
            const pltpInspections = inspectionData?.getInspectionsByPermit || [];
            const latestPltpInspection = pltpInspections.length > 0
               ? pltpInspections.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]
               : null;
            const pltpFindings = latestPltpInspection?.findings?.pltpFindings;

            if (!pltpFindings) {
               toast.error('Inspection findings not found');
               return;
            }
            // console.log('Inspection Findings:', pltpFindings);
         } else if (application.applicationType === 'Public Land Tree Cutting Permit') {
            const pltcpInspections = inspectionData?.getInspectionsByPermit || [];
            const latestPltcpInspection = pltcpInspections.length > 0
               ? pltcpInspections.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]
               : null;
            const pltcpFindings = latestPltcpInspection?.findings?.pltcpFindings;

            if (!pltcpFindings) {
               console.error('PLTCP Findings not found:', {
                  inspectionData,
                  latestPltcpInspection,
                  applicationData
               });
               toast.error('Inspection findings not found');
               return;
            }

            console.log('PLTCP Data:', {
               applicationData,
               pltcpFindings,
               treeInventory: pltcpFindings.treeInventory
            });

            let certificateInput = {
               applicationId: applicationData.id,
               applicationType: application.applicationType
            };

            certificateInput.pltcpCertificateData = {
               name: applicationData.name,
               occupation: applicationData.occupation || 'N/A',
               projectLocation: applicationData.projectLocation,
               projectAddress: applicationData.projectAddress,
               purpose: applicationData.purpose,
               treeInventory: pltcpFindings.treeInventory.map(tree => ({
                  species: tree.species,
                  scientificName: tree.scientificName,
                  numberOfTreesInventoried: tree.numberOfTreesInventoried,
                  grossVolume: tree.grossVolume,
                  netVolume: tree.netVolume,
                  treeCategory: tree.treeCategory
               })),
               totalTrees: pltcpFindings.totalTrees,
               totalGrossVolume: pltcpFindings.totalGrossVolume,
               totalNetVolume: pltcpFindings.totalNetVolume
            };

            console.log('Generated PLTCP Certificate Input:', certificateInput);
         }

         let certificateInput = {
            applicationId: applicationData.id,
            applicationType: application.applicationType
         };

         // Add type-specific data based on application type
         switch (application.applicationType) {
            case 'Chainsaw Registration':
               certificateInput.csawCertificateData = {
                  registrationType: applicationData.registrationType,
                  ownerName: applicationData.ownerName,
                  address: applicationData.address,
                  purpose: applicationData.purpose,
                  chainsawDetails: {
                     brand: applicationData.brand,
                     model: applicationData.model,
                     serialNumber: applicationData.serialNumber,
                     dateOfAcquisition: applicationData.dateOfAcquisition ?
                        new Date(parseInt(applicationData.dateOfAcquisition)).toISOString() : null,
                     powerOutput: applicationData.powerOutput,
                     maxLengthGuidebar: applicationData.maxLengthGuidebar,
                     countryOfOrigin: applicationData.countryOfOrigin,
                     purchasePrice: Number(applicationData.purchasePrice) || 0
                  }
               };
               break;

            case 'Certificate of Verification':
               const inspections = inspectionData?.getInspectionsByPermit || [];
               const latestInspection = inspections.length > 0
                  ? inspections.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]
                  : null;
               const covFindings = latestInspection?.findings?.covFindings;

               const volume = {
                  cubicMeters: Number(covFindings.volume?.cubicMeters) || 0,
                  boardFeet: Number(covFindings.volume?.boardFeet) || 0
               };

               certificateInput.covCertificateData = {
                  applicationNumber: applicationData.applicationNumber,
                  name: applicationData.name,
                  driverName: applicationData.driverName,
                  driverAddress: applicationData.driverAddress,
                  originAddress: applicationData.originAddress,
                  destinationAddress: applicationData.destinationAddress,
                  forestProducts: {
                     kind: covFindings.kind || '',
                     species: covFindings.species || '',
                     scientificName: covFindings.scientificName || '',
                     numberOfTrees: covFindings.numberOfTrees || 0,
                     quantity: {
                        value: covFindings.quantity?.value || 0,
                        unit: covFindings.quantity?.unit || 'pieces'
                     },
                     volume: {
                        cubicMeters: Number(volume.cubicMeters),
                        boardFeet: Number(volume.boardFeet)
                     }
                  },
                  conveyance: covFindings.conveyance || '',
                  vehiclePlateNumber: applicationData.vehiclePlateNumber || '',
                  consignee: covFindings.consignee || '',
                  permitNumber: applicationData.applicationNumber
               };
               break;

            case 'Private Tree Plantation Registration':
               const ptprInspections = inspectionData?.getInspectionsByPermit || [];
               const latestPtprInspection = ptprInspections.length > 0
                  ? ptprInspections.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]
                  : null;
               const ptprFindings = latestPtprInspection?.findings?.ptprFindings;

               if (!ptprFindings) {
                  toast.error('Inspection findings not found');
                  return;
               }

               certificateInput.ptprCertificateData = {
                  ownerName: applicationData.ownerName,
                  address: applicationData.address,
                  contactNumber: applicationData.contactNumber,
                  email: applicationData.email,
                  octTctNumber: ptprFindings.octTctNumber,
                  lotNumber: ptprFindings.lotNumber,
                  barangay: ptprFindings.barangay,
                  municipality: ptprFindings.municipality,
                  province: ptprFindings.province,
                  totalLotArea: ptprFindings.totalLotArea,
                  areaDevotedToPlantation: ptprFindings.areaDevotedToPlantation,
                  spacing: ptprFindings.spacing,
                  treeSpeciesPlanted: ptprFindings.treeSpeciesPlanted,
                  datePlanted: ptprFindings.datePlanted
               };
               break;

            case 'Private Land Timber Permit':
               const pltpInspections = inspectionData?.getInspectionsByPermit || [];
               const latestPltpInspection = pltpInspections.length > 0
                  ? pltpInspections.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]
                  : null;
               const pltpFindings = latestPltpInspection?.findings?.pltpFindings;

               if (!pltpFindings) {
                  toast.error('Inspection findings not found');
                  return;
               }

               certificateInput.pltpCertificateData = {
                  name: applicationData.name,
                  address: applicationData.address,
                  lotNumber: pltpFindings.lotNumber,
                  octNumber: pltpFindings.octNumber,
                  treeInventory: pltpFindings.treeInventory.map(tree => ({
                     species: tree.species,
                     scientificName: tree.scientificName,
                     numberOfTreesInventoried: tree.numberOfTreesInventoried,
                     treeStatus: tree.treeStatus,
                     grossVolume: tree.grossVolume,
                     netVolume: tree.netVolume
                  })),
                  totalTrees: pltpFindings.totalTrees,
                  totalGrossVolume: pltpFindings.totalGrossVolume,
                  totalNetVolume: pltpFindings.totalNetVolume
               };
               break;

            case 'Public Land Tree Cutting Permit':
               const pltcpInspections = inspectionData?.getInspectionsByPermit || [];
               const latestPltcpInspection = pltcpInspections.length > 0
                  ? pltcpInspections.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]
                  : null;
               const pltcpFindings = latestPltcpInspection?.findings?.pltcpFindings;

               if (!pltcpFindings) {
                  toast.error('Inspection findings not found');
                  return;
               }

               certificateInput.pltcpCertificateData = {
                  name: applicationData.name,
                  occupation: applicationData.occupation || 'N/A',
                  projectLocation: applicationData.projectLocation,
                  projectAddress: applicationData.projectAddress,
                  purpose: applicationData.purpose,
                  treeInventory: pltcpFindings.treeInventory.map(tree => ({
                     species: tree.species,
                     scientificName: tree.scientificName,
                     numberOfTreesInventoried: tree.numberOfTreesInventoried,
                     grossVolume: tree.grossVolume,
                     netVolume: tree.netVolume,
                     treeCategory: tree.treeCategory
                  })),
                  totalTrees: pltcpFindings.totalTrees,
                  totalGrossVolume: pltcpFindings.totalGrossVolume,
                  totalNetVolume: pltcpFindings.totalNetVolume
               };
               break;

            case 'Tree Cutting and/or Earth Balling Permit':
               const tcebpInspections = inspectionData?.getInspectionsByPermit || [];
               const latestTcebpInspection = tcebpInspections.length > 0
                  ? tcebpInspections.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]
                  : null;
               const tcebpFindings = latestTcebpInspection?.findings?.tcebpFindings;

               if (!tcebpFindings) {
                  console.error('TCEBP Findings not found:', {
                     inspectionData,
                     latestTcebpInspection,
                     applicationData
                  });
                  toast.error('Inspection findings not found. Please ensure an inspection has been completed.');
                  return;
               }

               console.log('TCEBP Template Data:', {
                  applicationData,
                  tcebpFindings,
                  treeInventory: tcebpFindings.treeInventory
               });

               certificateInput.tcebpCertificateData = {
                  name: applicationData.name,
                  officeName: applicationData.officeName,
                  officeAddress: applicationData.officeAddress,
                  projectLocation: applicationData.projectLocation,
                  contactNumber: applicationData.contactNumber,
                  purpose: applicationData.purpose,
                  requestType: applicationData.requestType,
                  treeInventory: tcebpFindings.treeInventory.map(tree => ({
                     species: tree.species,
                     numberOfTreesInventoried: tree.numberOfTreesInventoried,
                     status: tree.status,
                     treeCategory: tree.treeCategory
                  })),
                  totalTrees: tcebpFindings.totalTrees
               };
               break;
         }

         const { data } = await generateCertificateMutation({
            variables: {
               input: certificateInput
            }
         });

         if (data?.generateCertificate) {
            setGeneratedCertificate(data.generateCertificate);
            toast.success('E-Certificate generated successfully');
         }
      } catch (error) {
         console.error('Error generating certificate:', error);
         toast.error(`Failed to generate e-certificate: ${error.message}`);
      } finally {
         setIsGenerating(false);
      }
   };

   const renderCertificateTemplate = () => {
      if (!generatedCertificate) return null;

      switch (application.applicationType) {
         case 'Chainsaw Registration':
            return (
               <div style={{ display: 'none' }}>
                  <CSAWCertificateTemplate
                     ref={certificateRef}
                     certificate={generatedCertificate}
                     application={generatedCertificate.certificateData}
                     hiddenOnPrint={[]}
                  />
               </div>
            );
         case 'Certificate of Verification':
            return (
               <div style={{ display: 'none' }}>
                  <COVCertificateTemplate
                     ref={certificateRef}
                     certificate={generatedCertificate}
                     application={generatedCertificate.certificateData}
                     hiddenOnPrint={[]}
                  />
               </div>
            );
         case 'Private Tree Plantation Registration':
            return (
               <div style={{ display: 'none' }}>
                  <PTPRCertificateTemplate
                     ref={certificateRef}
                     certificate={generatedCertificate}
                     application={generatedCertificate.certificateData}
                     hiddenOnPrint={[]}
                  />
               </div>
            );
         case 'Public Land Tree Cutting Permit':
            return (
               <div style={{ display: 'none' }}>
                  <PLTCPCertificateTemplate
                     ref={certificateRef}
                     certificate={generatedCertificate}
                     application={generatedCertificate.certificateData}
                     hiddenOnPrint={[]}
                  />
               </div>
            );
         case 'Private Land Timber Permit':
            return (
               <div style={{ display: 'none' }}>
                  <PLTPCertificateTemplate
                     ref={certificateRef}
                     certificate={generatedCertificate}
                     application={generatedCertificate.c}
                     hiddenOnPrint={[]}
                  />
               </div>
            );
         case 'Tree Cutting and/or Earth Balling Permit':
            return (
               <div style={{ display: 'none' }}>
                  <TCEBPCertificateTemplate
                     ref={certificateRef}
                     certificate={generatedCertificate}
                     application={generatedCertificate.certificateData}
                     hiddenOnPrint={[]}
                  />
               </div>
            );
         default:
            return null;
      }
   };

   // Query to fetch existing certificate
   const { data: certificateData } = useQuery(GET_CERTIFICATE, {
      variables: { applicationId: application.id },
      skip: !application.id
   });

   // Set generated certificate if it exists
   useEffect(() => {
      if (certificateData?.getCertificatesByApplicationId?.[0]) {
         setGeneratedCertificate(certificateData.getCertificatesByApplicationId[0]);
      }
   }, [certificateData]);

   const handleDownload = async () => {
      try {
         const pdfBlob = await generateCSAWCertificatePDF({
            certificate: generatedCertificate
         });

         const url = window.URL.createObjectURL(pdfBlob);
         const link = document.createElement('a');
         link.href = url;
         link.download = `Certificate-${generatedCertificate.certificateNumber}.pdf`;
         document.body.appendChild(link);
         link.click();
         document.body.removeChild(link);
         window.URL.revokeObjectURL(url);

         toast.success('Certificate downloaded successfully');
      } catch (error) {
         console.error('Error downloading certificate:', error);
         toast.error('Failed to download certificate');
      }
   };

   const handleForward = async () => {
      try {
         await forwardCertificate({
            variables: {
               id: generatedCertificate.id,
               applicationType: application.applicationType
            }
         });

         const nextStage = application.applicationType === 'Certificate of Verification'
            ? 'PendingForestRangerSignature'
            : 'PendingSignatureByPENRCENROfficer';

         await updatePermitStage({
            variables: {
               id: application.id,
               currentStage: nextStage,
               status: 'In Progress',
               awaitingPermitCreation: false,
               PermitCreated: true,
               hasCertificate: true,
               certificateId: generatedCertificate.id
            }
         });

         const successMessage = application.applicationType === 'Certificate of Verification'
            ? 'Certificate forwarded to Forest Ranger for signature'
            : 'Certificate forwarded to PENR Officer for signature';

         toast.success(successMessage);
         onComplete();
         onClose();
      } catch (error) {
         console.error('Error forwarding certificate:', error);
         toast.error('Failed to forward certificate');
      }
   };

   // Add browser detection
   const isChrome = useMemo(() => {
      const userAgent = window.navigator.userAgent.toLowerCase();
      const isBrave = navigator.brave !== undefined;
      return userAgent.includes('chrome') && !userAgent.includes('edg') && !isBrave;
   }, []);

   // Add date picker handler
   const handleDateSelect = (date) => {
      setMetadata(prev => ({
         ...prev,
         issueDate: date ? format(date, 'yyyy-MM-dd') : ''
      }));
   };

   return (
      <Dialog open={isOpen} onOpenChange={onClose}>
         <DialogContent className={showECertificate ? "max-w-4xl" : "max-w-md"}>
            {showECertificate ? (
               <>
                  <DialogHeader>
                     <DialogTitle>E-Certificate Preview</DialogTitle>
                  </DialogHeader>

                  <div className="overflow-auto max-h-[70vh]">
                     {(() => {
                        switch (application.applicationType) {
                           case 'Chainsaw Registration':
                              return (
                                 <CSAWCertificateTemplate
                                    certificate={generatedCertificate}
                                    application={generatedCertificate.csawCertificateData}
                                    orderOfPayment={generatedCertificate.orderOfPayment}
                                    hiddenOnPrint={[]}
                                 />
                              );
                           case 'Certificate of Verification':
                              return (
                                 <COVCertificateTemplate
                                    certificate={generatedCertificate}
                                    application={generatedCertificate.certificateData}
                                    hiddenOnPrint={[]}
                                 />
                              );
                           case 'Private Tree Plantation Registration':
                              return (
                                 <PTPRCertificateTemplate
                                    certificate={generatedCertificate}
                                    application={generatedCertificate.certificateData}
                                    hiddenOnPrint={[]}
                                 />
                              );
                           case 'Public Land Tree Cutting Permit':
                              return (
                                 <PLTCPCertificateTemplate
                                    certificate={generatedCertificate}
                                    application={generatedCertificate.certificateData}
                                    hiddenOnPrint={[]}
                                 />
                              );
                           case 'Private Land Timber Permit':
                              return (
                                 <PLTPCertificateTemplate
                                    certificate={generatedCertificate}
                                    application={generatedCertificate.certificateData}
                                    hiddenOnPrint={[]}
                                 />
                              );
                           case 'Tree Cutting and/or Earth Balling Permit':
                              return (
                                 <TCEBPCertificateTemplate
                                    certificate={generatedCertificate}
                                    application={generatedCertificate.certificateData}
                                    hiddenOnPrint={[]}
                                 />
                              );
                           default:
                              return null;
                        }
                     })()}
                  </div>

                  <div className="flex justify-end gap-2">
                     <Button variant="outline" onClick={() => setShowECertificate(false)}>
                        Back
                     </Button>
                     {application.applicationType === 'Public Land Tree Cutting Permit' ? (
                        <>
                           <Button
                              onClick={handlePrint}
                              className="bg-blue-600 hover:bg-blue-700"
                           >
                              Print Page 1
                           </Button>
                           <Button
                              onClick={handlePrintPage2}
                              className="bg-blue-600 hover:bg-blue-700"
                           >
                              Print Page 2
                           </Button>
                        </>
                     ) : (
                        <Button
                           onClick={handlePrint}
                           className="bg-blue-600 hover:bg-blue-700"
                        >
                           Print Certificate
                        </Button>
                     )}
                     {application.applicationType === 'Chainsaw Registration' && (
                        <Button
                           onClick={handlePrintTerms}
                           variant="outline"
                        >
                           Print Terms
                        </Button>
                     )}
                  </div>
               </>
            ) : (
               <>
                  <DialogHeader>
                     <DialogTitle>Generate Certificate</DialogTitle>
                     <DialogDescription>
                        {application.applicationNumber} - {application.applicationType}
                     </DialogDescription>
                  </DialogHeader>

                  <div className="space-y-4 py-2">
                     {/* E-Certificate Generation */}
                     <div>
                        <Label className="text-sm font-medium mb-2 block">E-Certificate</Label>
                        <div className="flex gap-2">
                           <Button
                              onClick={generateECertificate}
                              className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white flex-1"
                              disabled={generatedCertificate !== null || isGenerating || application.hasCertificate}
                           >
                              {isGenerating ? (
                                 <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Generating...
                                 </>
                              ) : (
                                 <>
                                    <FileText className="h-4 w-4 mr-2" />
                                    Generate
                                 </>
                              )}
                           </Button>
                           {generatedCertificate && (
                              <Button
                                 onClick={() => setShowECertificate(true)}
                                 variant="outline"
                              >
                                 <Eye className="h-4 w-4 mr-2" />
                                 Preview
                              </Button>
                           )}
                        </div>
                     </div>

                     <Separator />

                     {/* Metadata Fields */}
                     <div className="grid gap-3">
                        <div>
                           <Label>Issue Date</Label>
                           {isChrome ? (
                              <Input
                                 type="date"
                                 name="issueDate"
                                 value={metadata.issueDate}
                                 onChange={handleMetadataChange}
                              />
                           ) : (
                              <Popover>
                                 <PopoverTrigger asChild>
                                    <Button
                                       variant={"outline"}
                                       className={cn(
                                          "w-full justify-start text-left font-normal",
                                          !metadata.issueDate && "text-muted-foreground"
                                       )}
                                    >
                                       <CalendarIcon className="mr-2 h-4 w-4" />
                                       {metadata.issueDate ? format(new Date(metadata.issueDate), "PPP") : "Pick a date"}
                                    </Button>
                                 </PopoverTrigger>
                                 <PopoverContent className="w-auto p-0">
                                    <Calendar
                                       mode="single"
                                       selected={metadata.issueDate ? new Date(metadata.issueDate) : undefined}
                                       onSelect={handleDateSelect}
                                       initialFocus
                                    />
                                 </PopoverContent>
                              </Popover>
                           )}
                        </div>
                        <div>
                           <Label>Remarks</Label>
                           <Input
                              type="text"
                              name="remarks"
                              value={metadata.remarks}
                              onChange={handleMetadataChange}
                              placeholder="Optional remarks"
                           />
                        </div>
                     </div>

                     {renderCertificateTemplate()}

                     <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={onClose}>
                           Cancel
                        </Button>
                        {generatedCertificate && (
                           <Button
                              onClick={handleForward}
                              className="bg-green-600 hover:bg-green-700"
                              disabled={!metadata.issueDate}
                           >
                              {application.applicationType === 'Certificate of Verification'
                                 ? 'Forward to Forest Ranger'
                                 : 'Forward to PENR Officer'}
                           </Button>
                        )}
                     </div>
                  </div>
               </>
            )}
         </DialogContent>
      </Dialog>
   );
};

export default TS_GenerateECertificateModal;